import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessageByCode } from '@/shared/utils/apiError';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@/shared/ui';
import { toast } from '@/shared/feedback/toast';
import { useClinicScope } from '@/shared/context/ClinicScopeContext';
import { useTimezone } from '@/shared/context/TimezoneContext';
import { usePermission } from '@/shared/hooks/usePermission';
import { useBookingOptions } from '@/modules/scheduling/hooks/useBookingOptions';
import { queryKeys } from '@/shared/query';
import AppointmentForm from './AppointmentForm';
import { appointmentAPI } from '../api/appointment.api';
import {
  buildCreateAppointmentPayload,
  EMPTY_APPOINTMENT_FORM_VALUES,
  type AppointmentFormValues,
} from '../utils/appointmentForm';
import {
  getBookingWarningMessages,
  isBookingWarningsError,
} from '../utils/bookingWarnings';

export type CreateAppointmentOverlayProps = {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void | Promise<void>;
  patientId?: string | null;
  leadId?: string | null;
  clinicId?: string | null;
  doctorId?: string | null;
  date?: string | null;
  startTime?: string | null;
  /** Calendar slot opened with a booking warning. */
  showPrefillWarnBanner?: boolean;
  /** Lock subject picker to the given patient (Patient Details). */
  lockPatient?: boolean;
  /** Lock subject picker to the given lead (inbox). */
  lockLead?: boolean;
};

/**
 * Appointment create dialog.
 * The doctor roster is fetched while the host page is open so the dialog can paint from cache.
 */
const CreateAppointmentOverlay: React.FC<CreateAppointmentOverlayProps> = ({
  open,
  onClose,
  onCreated,
  patientId,
  leadId,
  clinicId,
  doctorId,
  date,
  startTime,
  showPrefillWarnBanner = false,
  lockPatient = false,
  lockLead = false,
}) => {
  const { t } = useTranslation(['appointment', 'scheduling', 'common']);
  const { hasPermission } = usePermission();
  const { clinics, selected: scopeSelected } = useClinicScope();
  const queryClient = useQueryClient();
  const warmClinicId =
    clinicId ||
    (scopeSelected !== 'all'
      ? scopeSelected
      : clinics.length === 1
        ? clinics[0]._id
        : '');
  // Same key as the form's undated doctors query. Runs before open so click hits cache.
  useBookingOptions(
    hasPermission('appointments:create') && warmClinicId ? { clinicId: warmClinicId } : null
  );
  const { zonedDateTimeToUtc } = useTimezone();
  const pendingValuesRef = useRef<AppointmentFormValues | null>(null);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  // Stay mounted through the leave transition. Unmounting on close skips it.
  const [present, setPresent] = useState(open);
  useEffect(() => {
    if (open) setPresent(true);
  }, [open]);

  const prefillPatientId = patientId || null;
  const prefillLeadId = prefillPatientId ? null : leadId || null;

  const initialValues = useMemo<AppointmentFormValues>(() => {
    if (prefillPatientId) {
      return {
        ...EMPTY_APPOINTMENT_FORM_VALUES,
        subjectType: 'patient',
        patientId: prefillPatientId,
      };
    }
    if (prefillLeadId) {
      return {
        ...EMPTY_APPOINTMENT_FORM_VALUES,
        subjectType: 'lead',
        leadId: prefillLeadId,
      };
    }
    return EMPTY_APPOINTMENT_FORM_VALUES;
  }, [prefillLeadId, prefillPatientId]);

  const createMutation = useMutation({
    mutationFn: ({
      values,
      confirmWarnings,
    }: {
      values: AppointmentFormValues;
      confirmWarnings?: boolean;
    }) =>
      appointmentAPI.createAppointment({
        ...buildCreateAppointmentPayload(values, zonedDateTimeToUtc),
        ...(confirmWarnings ? { confirmWarnings: true } : {}),
      }),
    onSuccess: () => {
      pendingValuesRef.current = null;
      setWarningMessages([]);
      toast.success(t('toasts.addSuccess'));
      onClose();
      void queryClient.invalidateQueries({ queryKey: queryKeys.appointment.all });
      void onCreated?.();
    },
    onError: (err) => {
      if (isBookingWarningsError(err)) {
        setWarningMessages(getBookingWarningMessages(err));
        return;
      }
      toast.error(
        getApiErrorMessageByCode(err, t('toasts.addFailed'), {
          staff_busy: t('scheduling:busyTime.errors.staff_busy'),
        })
      );
    },
  });

  const submitError =
    createMutation.isError && !isBookingWarningsError(createMutation.error)
      ? getApiErrorMessageByCode(createMutation.error, t('toasts.addFailedRetry'), {
          staff_busy: t('scheduling:busyTime.errors.staff_busy'),
        })
      : null;

  const handleSubmit = (values: AppointmentFormValues) => {
    pendingValuesRef.current = values;
    createMutation.mutate({ values });
  };

  const handleConfirmWarnings = () => {
    const values = pendingValuesRef.current;
    if (!values) return;
    setWarningMessages([]);
    createMutation.mutate({ values, confirmWarnings: true });
  };

  if (!present) return null;

  const handleExited = () => setPresent(false);

  if (!hasPermission('appointments:create')) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onExited: handleExited }}>
        <DialogTitle>{t('pages.addTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="error">{t('pages.createPermissionDenied')}</Alert>
        </DialogContent>
        <DialogActions>
          <Button intent="neutral" onClick={onClose}>
            {t('common:cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <AppointmentForm
        mode="create"
        presentation="dialog"
        lockPatient={lockPatient}
        lockLead={lockLead}
        title={t('pages.addTitle')}
        onBack={onClose}
        submitLabel={t('actions.scheduleAppointment')}
        submittingLabel={t('actions.scheduling')}
        submitting={createMutation.isPending}
        error={submitError}
        initialValues={initialValues}
        prefillPatientId={prefillPatientId}
        prefillLeadId={prefillLeadId}
        prefillClinicId={clinicId}
        prefillDoctorId={doctorId}
        prefillDate={date}
        prefillStartTime={startTime}
        showPrefillWarnBanner={showPrefillWarnBanner}
        onSubmit={handleSubmit}
        dialogOpen={open}
        onDialogExited={handleExited}
      />

      <Dialog
        open={warningMessages.length > 0}
        onClose={() => setWarningMessages([])}
        aria-labelledby="inbox-booking-warnings-title"
      >
        <DialogTitle id="inbox-booking-warnings-title">
          {t('bookingWarnings.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            {t('bookingWarnings.body')}
          </DialogContentText>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {warningMessages.map((message) => (
              <Typography component="li" key={message} variant="body2">
                {message}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningMessages([])} intent="secondary">
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleConfirmWarnings}
            intent="primary"
            disabled={createMutation.isPending}
          >
            {t('bookingWarnings.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateAppointmentOverlay;
